/**
 *
 * CheckoutStep3Page
 *
 */

import messages from 'containers/CheckoutPage/messages';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import translations from 'translations';

import CheckoutStep3 from 'components/Checkout/CheckoutStep3';
import { push } from 'connected-react-router';
import { checkoutCart, goToStep, setSomethingChanged } from 'containers/CheckoutPage/actions';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import reducer from './reducer';
import saga from './saga';
import { selectCheckout, selectLoadingOrderDrafts, selectOrderDrafts } from './selectors';

import {
  selectDiscountedValue,
  selectLoadingCheckout,
  selectOpenModalSomthingChanged,
  selectVoucherStatus,
} from 'containers/CheckoutPage/selectors';
import { selectCart, selectOrderDisabled, selectStore } from 'containers/MainLayout/selectors';
import React from 'react';
import { OrderDisabledResponse, OrderDraft, Store } from 'types/schema';
import { VOUCHER_STATUS } from '../CheckoutPage/constants';
import { RootState } from './types';

interface OwnProps {
  cart: any;
}

interface StateProps {
  cart: any;
  store: Store;
  checkout: any;
  loading: boolean;
  checkoutLoading: boolean;
  orderDisabled: OrderDisabledResponse;
  orderDrafts: OrderDraft[];
  openModalSomthingChanged: boolean;
  discountedValue: number;
  voucherStatus: VOUCHER_STATUS | null;
}

interface DispatchProps {
  onCheckout: () => void;
  onGoPreviousStep: () => void;
  onGoToStep1: (data: boolean) => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const CheckoutStep3Page = (props: Props) => {
  const {
    loading,
    checkoutLoading,
    onCheckout,
    onGoPreviousStep,
    checkout,
    cart,
    orderDisabled,
    store,
    onGoToStep1,
    discountedValue,
    voucherStatus,
  } = props;

  return (
    <>
      <Helmet>
        <title>{translations(messages.headerStep3)}</title>
        <meta name="description" content={`${translations(messages.headerStep3)}`} />
      </Helmet>
      <CheckoutStep3
        loading={loading}
        checkoutLoading={checkoutLoading}
        onCheckout={onCheckout}
        onGoPreviousStep={onGoPreviousStep}
        checkout={checkout}
        cart={cart}
        orderDisabled={orderDisabled.orderDisabled}
        store={store}
        openModalSomthingChanged={props.openModalSomthingChanged}
        onGoToStep1={onGoToStep1}
        discountedValue={discountedValue}
        voucherStatus={voucherStatus}
      />
    </>
  );
};

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  orderDrafts: selectOrderDrafts(),
  checkout: selectCheckout(),
  loading: selectLoadingOrderDrafts(),
  checkoutLoading: selectLoadingCheckout(),
  orderDisabled: selectOrderDisabled(),
  store: selectStore(),
  openModalSomthingChanged: selectOpenModalSomthingChanged(),
  cart: selectCart(),
  discountedValue: selectDiscountedValue(),
  voucherStatus: selectVoucherStatus(),
});

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps): DispatchProps {
  return {
    onGoPreviousStep: () => dispatch(push('/checkout/step/2')),
    onCheckout: () => dispatch(checkoutCart()),
    onGoToStep1: (hasSomethingChange = true) => {
      if (hasSomethingChange) {
        dispatch(setSomethingChanged());
      }
      dispatch(goToStep(1));
    },
  };
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const {
    orderDrafts,
    checkout,
    loading,
    checkoutLoading,
    store,
    openModalSomthingChanged,
    cart,
    discountedValue,
    voucherStatus,
  } = stateProps;
  return {
    cart: {
      cartSet: orderDrafts,
      deliveryFee: cart?.deliveryFee,
      loading: cart?.loading,
      store: cart?.store,
    },
    checkout: checkout,
    loading: loading,
    checkoutLoading: checkoutLoading,
    ...dispatchProps,
    ...ownProps,
    orderDisabled: stateProps.orderDisabled,
    store: store,
    openModalSomthingChanged: openModalSomthingChanged,
    discountedValue: discountedValue,
    voucherStatus: voucherStatus,
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps, mergeProps);

// <OwnProps> restricts access to the HOC's other props. This component must not do anything with reducer hoc
const withReducer = injectReducer<OwnProps>({
  key: 'checkoutStep3Page',
  reducer: reducer,
});
// <OwnProps> restricts access to the HOC's other props. This component must not do anything with saga hoc
const withSaga = injectSaga<OwnProps>({ key: 'checkoutStep3Page', saga: saga });

export default compose(withReducer, withSaga, withConnect)(CheckoutStep3Page);
